import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User, AuthError } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export type UserRole = "customer" | "florist" | "admin";

interface AuthUser extends User {
  user_metadata: {
    full_name: string;
    role: UserRole;
  };
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFloristVerified, setIsFloristVerified] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user as AuthUser ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user as AuthUser ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Check store profile whenever user changes
  useEffect(() => {
    const checkStoreProfile = async () => {
      if (!user?.id) {
        setIsFloristVerified(false);
        return;
      }

      const { data: profile } = await supabase
        .from("store_profiles")
        .select("*")
        .eq("florist_id", user.id)
        .single();

      setIsFloristVerified(!!profile && user?.user_metadata?.role === "florist");
    };

    checkStoreProfile();
  }, [user]);

  const checkFloristStatus = async (userId: string) => {
    const { data: floristProfile, error } = await supabase
      .from("store_profiles")
      .select("*")
      .eq("florist_id", userId)
      .single();

    if (error) {
      console.error("Error checking florist status:", error);
      return null;
    }

    return floristProfile;
  };

  const signUp = async (email: string, password: string, fullName: string, role: UserRole = "customer") => {
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role,
          },
        },
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error("No user data returned");
      }

      if (role === "florist") {
        const { error: profileError } = await supabase
          .from("store_profiles")
          .insert({
            id: authData.user.id,
            store_name: "",
            street_address: "",
            suburb: "",
            state: "",
            postcode: "",
            store_status: "private",
            setup_progress: 0
          });

        if (profileError) throw profileError;
      }
      
      toast.success("Account created successfully!");
      
      if (role === "florist") {
        navigate("/become-florist");
      } else {
        navigate("/dashboard");
      }

      return { data: authData, error: null };
    } catch (error: any) {
      console.error("Signup error:", error);
      toast.error(error.message || "Failed to create account");
      return { data: null, error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      const role = data.user?.user_metadata?.role;
      
      toast.success("Signed in successfully!");
      
      if (role === "florist") {
        navigate("/florist-dashboard");
      } else if (role === "customer") {
        navigate("/customer-dashboard");
      } else {
        navigate("/dashboard");
      }

      return { data, error: null };
    } catch (error: any) {
      console.error("Sign in error:", error);
      toast.error(error.message || "Failed to sign in");
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setSession(null);
      toast.success("Signed out successfully");
      navigate("/");
    } catch (error: any) {
      console.error("Sign out error:", error);
      toast.error(error.message || "Failed to sign out");
    }
  };

  const updateUserRole = async (userId: string, role: UserRole) => {
    try {
      const { data, error } = await supabase.functions.invoke('update-user-role', {
        body: { userId }
      });

      if (error) throw error;

      // Update local user state
      setUser(prev => prev ? {
        ...prev,
        user_metadata: {
          ...prev.user_metadata,
          role
        }
      } : null);

      toast.success(`Role updated to ${role}`);
      return { data, error: null };
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast.error(error.message || "Failed to update role");
      return { data: null, error };
    }
  };

  // Add this effect to fix the role when needed
  useEffect(() => {
    const fixRole = async () => {
      if (!user) return;
      
      // Check if this is the specific user we want to fix
      if (user.id === '97be634d-d7b6-442b-a8b6-f9e3ff3582d9' && user.user_metadata?.role !== 'florist') {
        console.log('Fixing florist role for user:', user.id);
        await updateUserRole(user.id, 'florist');
      }
    };

    fixRole();
  }, [user]);

  const isFlorist = isFloristVerified;
  const isAdmin = user?.user_metadata?.role === "admin";
  const isCustomer = user?.user_metadata?.role === "customer";

  return {
    user,
    session,
    loading,
    isFlorist,
    isAdmin,
    isCustomer,
    signUp,
    signIn,
    signOut,
    updateUserRole,
  };
} 